import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  players: [
    { name: 'Spiller 1', firstCategoryScores: {}, secondCategoryScores: {}, disabledCategories: {} },
  ],
  numberOfPlayers: 1,
  gameType: 'fiveDice',
  minValueForBonus: 63,
  tab: 0,
  showCategoryModal: false,
  category: '',
  placement: '',
  showTurnDialog: false,
  lastActiveTab: 0,
  showGameOverModal: false,
  isGameAction: false,
};

const yatzySlice = createSlice({
  name: 'yatzy',
  initialState,
  reducers: {
    setPlayers: (state, action) => {
      state.players = action.payload;
    },
    setNumberOfPlayers: (state, action) => {
      state.numberOfPlayers = action.payload;
    },
    setGameType: (state, action) => {
      state.gameType = action.payload;
      // Update minValueForBonus based on gameType
      state.minValueForBonus = action.payload === 'sixDice' ? 84 : 63;
    },
    setTab: (state, action) => {
      state.tab = action.payload;
    },
    setShowCategoryModal: (state, action) => {
      state.showCategoryModal = action.payload;
    },
    setCategory: (state, action) => {
      state.category = action.payload;
    },
    setPlacement: (state, action) => {
      state.placement = action.payload;
    },
    setShowTurnDialog: (state, action) => {
      state.showTurnDialog = action.payload;
    },
    setLastActiveTab: (state, action) => {
      state.lastActiveTab = action.payload;
    },
    setShowGameOverModal: (state, action) => {
      state.showGameOverModal = action.payload;
    },
    setIsGameAction: (state, action) => {
      state.isGameAction = action.payload;
    },
    updatePlayerName: (state, action) => {
      const { index, name } = action.payload;
      state.players[index].name = name;
    },
    updateScore: (state, action) => {
      const { playerIndex, scoreType, categoryName, value } = action.payload;
      state.players[playerIndex][`${scoreType}CategoryScores`][categoryName] = value;
    },
    disableCategory: (state, action) => {
      const { playerIndex, categoryName } = action.payload;
      state.players[playerIndex].disabledCategories[categoryName] = true;
      // Set score to 0 when disabling
      const scoreType = state.placement === 'first' ? 'first' : 'second';
      state.players[playerIndex][`${scoreType}CategoryScores`][categoryName] = 0;
    },
    removeDisabledCategory: (state, action) => {
      const { playerIndex, categoryName } = action.payload;
      delete state.players[playerIndex].disabledCategories[categoryName];
    },
    resetGameKeepNames: (state) => {
      const resetPlayers = state.players.map(player => ({
        name: player.name,
        firstCategoryScores: {},
        secondCategoryScores: {},
        disabledCategories: {},
      }));
      state.players = resetPlayers;
      state.tab = 0;
      state.showCategoryModal = false;
      state.showGameOverModal = false;
      state.showTurnDialog = false;
    },
    nextPlayer: (state, action) => {
      const playerIndex = action.payload;
      const nextTab = (playerIndex + 1) % state.numberOfPlayers;
      state.tab = nextTab;
      state.isGameAction = true;
    },
    // Add new players when numberOfPlayers increases
    addPlayers: (state) => {
      const currentPlayers = [...state.players];
      if (state.numberOfPlayers > currentPlayers.length) {
        const newPlayers = Array.from({ length: state.numberOfPlayers - currentPlayers.length }, (_, index) => ({
          name: `Spiller ${currentPlayers.length + index + 1}`,
          firstCategoryScores: {},
          secondCategoryScores: {},
          disabledCategories: {},
        }));
        state.players = [...currentPlayers, ...newPlayers];
      } else if (state.numberOfPlayers < currentPlayers.length) {
        // Remove players from the end while preserving existing data
        const updatedPlayers = currentPlayers.slice(0, state.numberOfPlayers).map(player => ({
          ...player,
          firstCategoryScores: { ...player.firstCategoryScores },
          secondCategoryScores: { ...player.secondCategoryScores },
          disabledCategories: { ...player.disabledCategories }
        }));
        state.players = updatedPlayers;
      }
    },
    // Ensure tab is valid when number of players changes
    validateTab: (state) => {
      if (state.tab >= state.numberOfPlayers) {
        state.tab = Math.max(0, state.numberOfPlayers - 1);
      }
    },
  },
});

export const {
  setPlayers,
  setNumberOfPlayers,
  setGameType,
  setTab,
  setShowCategoryModal,
  setCategory,
  setPlacement,
  setShowTurnDialog,
  setLastActiveTab,
  setShowGameOverModal,
  setIsGameAction,
  updatePlayerName,
  updateScore,
  disableCategory,
  removeDisabledCategory,
  resetGameKeepNames,
  nextPlayer,
  addPlayers,
  validateTab,
} = yatzySlice.actions;

export default yatzySlice.reducer;
